import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    MessageSquare,
    Send,
    CheckCircle,
    Clock,
    AlertCircle,
    Package,
    Mail,
    User,
    Building
} from 'lucide-react';
import { get, post } from '../../api/apiClient';
import toast from 'react-hot-toast';
import { formatDate } from "../../utils/stringUtils";

const PublicTicketView = () => {
    const { ticketId } = useParams();
    const [replyMessage, setReplyMessage] = useState('');
    const queryClient = useQueryClient();

    // Fetch ticket details
    const { data: ticketData, isLoading, error } = useQuery({
        queryKey: ['publicTicket', ticketId],
        queryFn: async () => {
            const response = await get(`/crm/tickets/public/${ticketId}`);
            if (response.responseCode === 200) {
                return response.data;
            }
            throw new Error(response.responseMessage || 'Failed to fetch ticket');
        },
        retry: 1
    });

    // Submit reply mutation
    const replyMutation = useMutation({
        mutationFn: async (replyData) => {
            const response = await post(`/crm/tickets/public/${ticketId}/reply`, replyData);
            if (response.responseCode === 200 || response.responseCode === 201) {
                return response.data;
            }
            throw new Error(response.responseMessage || 'Failed to submit reply');
        },
        onSuccess: () => {
            toast.success('Reply submitted successfully');
            setReplyMessage('');
            queryClient.invalidateQueries(['publicTicket', ticketId]);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to submit reply');
        }
    });

    const handleSubmitReply = (e) => {
        e.preventDefault();

        if (!replyMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }

        replyMutation.mutate({
            message: replyMessage.trim(),
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'in-progress':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'pending':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'resolved':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'closed':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'open':
                return <AlertCircle className="h-4 w-4" />;
            case 'in-progress':
            case 'pending':
                return <Clock className="h-4 w-4" />;
            case 'resolved':
            case 'closed':
                return <CheckCircle className="h-4 w-4" />;
            default:
                return <AlertCircle className="h-4 w-4" />;
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading ticket details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8 text-center">
                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
                    <p className="text-gray-600 mb-4">{error.message || 'Unable to load ticket details'}</p>
                    <p className="text-sm text-gray-500">Please check the ticket ID and try again.</p>
                </div>
            </div>
        );
    }

    const ticket = ticketData?.ticket;


    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                {/* Header */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
                    {/* Top Bar with Logo */}
                    <div className="bg-gradient-to-r from-agrosaf-green to-agrosaf-darkGreen px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="bg-white p-2 rounded-lg shadow-sm">
                                    <img
                                        src="/logo.png"
                                        alt="Company Logo"
                                        className="h-10 w-10 object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">
                                        {process.env.REACT_APP_COMPANY_NAME}
                                    </h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Ticket Title & ID */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">{ticket?.title}</h1>
                                <div className="flex items-center space-x-4 text-sm">
                                    <span className="text-gray-600">Ticket ID:</span>
                                    <span className="font-semibold text-agrosaf-green">{ticket?.ticketId}</span>
                                </div>
                            </div>
                            <div className={`px-4 py-2 rounded-full border flex items-center gap-2 ${getStatusColor(ticket?.status)}`}>
                                {getStatusIcon(ticket?.status)}
                                <span className="font-medium capitalize">{ticket?.status?.replace('-', ' ')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Info Bar */}
                    <div className="px-6 py-3 bg-white">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
                            <div className="flex flex-wrap items-center gap-6">
                                <div className="flex items-center space-x-2">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-500">Created On:</span>
                                    <span className="font-medium text-gray-900">
                                        {formatDate(ticket?.createdAt, { includeTime: false })}
                                    </span>
                                </div>
                                {ticket?.orderId && (
                                    <div className="flex items-center space-x-2">
                                        <Package className="h-4 w-4 text-gray-500" />
                                        <span className="text-gray-500">Order ID:</span>
                                        <span className="font-medium text-gray-900">
                                            {ticket.orderId?.orderNumber || ticket.orderId?.orderId || 'N/A'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex items-start space-x-3 min-w-0">
                            <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-600">Customer Name</p>
                                <p className="font-medium text-gray-900 break-words capitalize">
                                    {ticket?.customerName || ticket?.customerId?.contactPerson || 'N/A'}
                                </p>
                            </div>
                        </div>
                        {(ticket?.customerId?.email) && (
                            <div className="flex items-start space-x-3 min-w-0">
                                <Mail className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-600">Email</p>
                                    <p className="font-medium text-gray-900 break-words">
                                        {ticket.customerId.email}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Description */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 shadow-sm">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">Description</h2>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words break-all overflow-wrap-anywhere">{ticket?.description}</p>
                </div>

                {/* Conversation Section */}
                {(ticket?.comment && ticket.comment.length > 0) && (
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <MessageSquare className="h-6 w-6 text-agrosaf-green" />
                                History
                            </h3>
                            <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full border border-gray-200">
                                {ticket.comment.length} {ticket.comment.length === 1 ? 'Message' : 'Messages'}
                            </span>
                        </div>

                        <div className="flex flex-col space-y-6 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                            {ticket.comment.map((comment, index) => {
                                const isAgent = !!comment.agent;
                                const message = isAgent ? comment.agent : comment.customer;
                                const timestamp = comment.timestamp || comment.createdAt;

                                return (
                                    <div
                                        key={comment._id || index}
                                        className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`w-[90%] sm:w-[85%] bg-gray-50 p-4 shadow-sm ${isAgent
                                            ? 'border-r-4 border-agrosaf-green rounded-l-lg'
                                            : 'border-l-4 border-blue-500 rounded-r-lg'
                                            }`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isAgent ? 'bg-agrosaf-green' : 'bg-blue-500'
                                                        }`}>
                                                        <span className="text-white text-sm font-medium">
                                                            {isAgent ? 'S' : 'C'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 capitalize">
                                                            {isAgent ? 'Support Team' : (ticket?.customerName || 'Customer')}
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(timestamp)}
                                                </span>
                                            </div>
                                            <p className="text-gray-700 text-sm whitespace-pre-wrap break-words break-all overflow-wrap-anywhere leading-relaxed">
                                                {message}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Reply Form */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Send className="h-5 w-5 text-blue-600" />
                        Reply
                    </h3>
                    <form onSubmit={handleSubmitReply} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Message <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value.slice(0, 500))}
                                placeholder="Type your message or question here..."
                                rows={4}
                                maxLength={500}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {replyMessage.length} / 500 characters
                            </p>
                        </div>

                        <button
                            type="submit"
                            disabled={replyMutation.isPending || !replyMessage.trim()}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                        >
                            {replyMutation.isPending ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="h-5 w-5" />
                                    Send
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    <p>Need help? Our support team is here to assist you.</p>
                </div>
            </div>
        </div>
    );
};

export default PublicTicketView;
